// File: src/pages/ContactPage.js
import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import {
    Form,
    Input,
    Button,
    Radio,
    Checkbox,
    Row,
    Col,
    Card,
    Typography,
    Alert,
    Result,
    Space,
} from 'antd';
import {
    PhoneOutlined,
    MailOutlined,
    EnvironmentOutlined,
    CheckCircleOutlined,
    WarningOutlined,
} from '@ant-design/icons';

const { Title, Paragraph, Text } = Typography;
const { TextArea } = Input;

const ContactPage = () => {
    const [form] = Form.useForm();
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitSuccess, setSubmitSuccess] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [userType, setUserType] = useState('user');
    const [backendUrl, setBackendUrl] = useState('');

    useEffect(() => {
        const url = process.env.REACT_APP_API_URL || 'http://localhost:5000';
        setBackendUrl(url);
    }, []);

    const onFinish = async (values) => {
        setIsSubmitting(true);
        setSubmitError(null);

        const { name, email, subject, message } = values;
        const payload = { name, email, subject, message, userType };

        try {
            const apiUrl = new URL('/api/contact', backendUrl);

            const response = await fetch(apiUrl.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify(payload),
            });

            const result = await response.json();

            if (!response.ok) {
                throw new Error(result.message || `HTTP error! status: ${response.status}`);
            }

            console.log('Form submitted successfully:', result.data);
            setSubmitSuccess(true);
            form.resetFields();

        } catch (error) {
            console.error('Error submitting form:', error);
            setSubmitError(error.message || 'Beim Senden Ihrer Nachricht ist ein Fehler aufgetreten. Bitte versuchen Sie es später erneut.');
        } finally {
            setIsSubmitting(false);
        }
    };

    const onFinishFailed = (errorInfo) => {
        console.log('Failed:', errorInfo);
    };

    const handleUserTypeChange = (e) => {
        setUserType(e.target.value);
    };

    const handleNewMessage = () => {
        setSubmitSuccess(false);
        setSubmitError(null);
    };

    return (
        <div className="contact-page" style={{ padding: '40px 20px' }}>
            <Helmet>
                {/* <title>Kontakt | Snus Finder</title> */}
                <title>Kontakt | Snus Verzeichnis Deutschland</title>
                {/* <meta name="description" content="Kontaktieren Sie das Snus Finder Team. Wir helfen Ihnen gerne bei Fragen zur Nutzung unseres Verzeichnisses oder zur Shop-Registrierung." /> */}
                <meta name="description" content="Nehmen Sie Kontakt mit dem Team von Snus Verzeichnis Deutschland auf. Wir freuen uns auf Ihre Nachricht." />
                <meta name="robots" content="index, follow" />
                <link rel="canonical" href="https://www.snus-finder.de/contact" />
            </Helmet>

            <Row justify="center" style={{ marginBottom: '40px' }}>
                <Col xs={24} md={20} lg={16} style={{ textAlign: 'center' }}>
                    <Title level={1}>Kontakt</Title>
                    <Paragraph className="lead">Wir sind für Sie da. Wie können wir Ihnen helfen?</Paragraph>
                </Col>
            </Row>

            <Row gutter={[32, 32]} justify="center">
                <Col xs={24} md={10} lg={8}>
                    <Title level={3} style={{ marginBottom: '24px' }}>Kontaktinformationen</Title>
                    <Space direction="vertical" size="large" style={{ width: '100%' }}>
                        <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                            <Space align="start">
                                <PhoneOutlined style={{ fontSize: '24px', color: '#1890ff', marginTop: '4px' }} />
                                <div>
                                    <Title level={5}>Telefonisch</Title>
                                    <Paragraph>01733958762</Paragraph>
                                    <Text type="secondary">Mo-Fr: 9:00 - 17:00 Uhr</Text>
                                </div>
                            </Space>
                        </Card>

                        <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                             <Space align="start">
                                <MailOutlined style={{ fontSize: '24px', color: '#1890ff', marginTop: '4px' }} />
                                <div>
                                    <Title level={5}>E-Mail</Title>
                                    <Paragraph>info.snusfinder@gmail.com</Paragraph>
                                </div>
                             </Space>
                        </Card>

                        <Card bordered={false} style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                            <Space align="start">
                                <EnvironmentOutlined style={{ fontSize: '24px', color: '#1890ff', marginTop: '4px' }} />
                                <div>
                                    <Title level={5}>Adresse</Title>
                                    <Paragraph>Heinz Consulting</Paragraph>
                                    <Paragraph>Knollstraße 17</Paragraph>
                                    <Paragraph>72072 Tübingen</Paragraph>
                                </div>
                            </Space>
                        </Card>
                    </Space>
                </Col>

                <Col xs={24} md={14} lg={12}>
                    <Card style={{ boxShadow: '0 2px 8px rgba(0, 0, 0, 0.1)' }}>
                        {submitSuccess ? (
                            <Result
                                status="success"
                                icon={<CheckCircleOutlined />}
                                title="Nachricht erfolgreich gesendet!"
                                subTitle="Vielen Dank für Ihre Nachricht. Wir werden uns so schnell wie möglich bei Ihnen melden."
                                extra={[
                                    <Button type="primary" key="new" onClick={handleNewMessage}>
                                        Neue Nachricht senden
                                    </Button>,
                                ]}
                            />
                        ) : (
                            <>
                                <Title level={3} style={{ marginBottom: '8px' }}>Kontaktformular</Title>
                                <Paragraph type="secondary" style={{ marginBottom: '24px' }}>
                                    Haben Sie Fragen oder Anregungen? Füllen Sie einfach das Formular aus, und wir melden uns bei Ihnen.
                                </Paragraph>

                                <Radio.Group
                                    onChange={handleUserTypeChange}
                                    value={userType}
                                    style={{ marginBottom: 24 }}
                                >
                                    <Radio.Button value="user">Ich bin Nutzer</Radio.Button>
                                    <Radio.Button value="shop">Ich bin Shop-Betreiber</Radio.Button>
                                </Radio.Group>

                                {submitError && (
                                    <Alert
                                        message="Fehler"
                                        description={submitError}
                                        type="error"
                                        showIcon
                                        icon={<WarningOutlined />}
                                        style={{ marginBottom: 24 }}
                                        closable
                                        onClose={() => setSubmitError(null)}
                                    />
                                )}

                                <Form
                                    form={form}
                                    layout="vertical"
                                    name="contact-form"
                                    onFinish={onFinish}
                                    onFinishFailed={onFinishFailed}
                                >
                                    <Form.Item
                                        label="Name"
                                        name="name"
                                        rules={[{ required: true, message: 'Bitte geben Sie Ihren Namen ein', whitespace: true }]}
                                    >
                                        <Input placeholder="Ihr Name" />
                                    </Form.Item>

                                    <Form.Item
                                        label="E-Mail"
                                        name="email"
                                        rules={[
                                            { required: true, message: 'Bitte geben Sie Ihre E-Mail-Adresse ein' },
                                            { type: 'email', message: 'Bitte geben Sie eine gültige E-Mail-Adresse ein' },
                                        ]}
                                    >
                                        <Input type="email" placeholder="Ihre E-Mail-Adresse" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Betreff"
                                        name="subject"
                                        rules={[{ required: true, message: 'Bitte geben Sie einen Betreff ein', whitespace: true }]}
                                    >
                                        <Input placeholder="Betreff Ihrer Nachricht" />
                                    </Form.Item>

                                    <Form.Item
                                        label="Nachricht"
                                        name="message"
                                        rules={[
                                            { required: true, message: 'Bitte geben Sie eine Nachricht ein', whitespace: true },
                                            { min: 10, message: 'Ihre Nachricht sollte mindestens 10 Zeichen enthalten' }
                                        ]}
                                    >
                                        <TextArea rows={6} placeholder="Ihre Nachricht an uns..." />
                                    </Form.Item>

                                    <Form.Item
                                        name="agreement"
                                        valuePropName="checked"
                                        rules={[
                                            {
                                                validator: (_, value) =>
                                                    value ? Promise.resolve() : Promise.reject(new Error('Sie müssen den Datenschutzbestimmungen zustimmen')),
                                            },
                                        ]}
                                    >
                                        <Checkbox>
                                            Ich habe die <a href="/privacy" target="_blank" rel="noopener noreferrer">Datenschutzbestimmungen</a> gelesen und stimme der Verarbeitung meiner Daten zu. *
                                        </Checkbox>
                                    </Form.Item>

                                    <Form.Item>
                                        <Button
                                            type="primary"
                                            htmlType="submit"
                                            loading={isSubmitting}
                                            block
                                        >
                                            {isSubmitting ? 'Wird gesendet...' : 'Nachricht senden'}
                                        </Button>
                                    </Form.Item>
                                </Form>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>
        </div>
    );
};

export default ContactPage;